/**
 * Quote-specific overweight thresholds (V1 formatview_quote.pl:820-911,
 * fv_lbs / fv_kgs). A weight STRICTLY greater than its size/triaxle/OTR limit is
 * overweight (lib_header.pl chkoverweight uses `wgt > limit`).
 *
 * NOTE: this is the QUOTE matrix, deliberately separate from the order-side
 * thresholds (now canonical in the backend app/core/oog_specs.py, reached via
 * POST /oog/compute) — the limits differ.
 */

export interface WeightFields {
  size?: string | null;
  lbs?: string | number | null;
  kgs?: string | number | null;
  triaxle?: string | null;
  deliveryZone?: string | null;
}

// V1 size buckets: the 40-ish branch (`size=~/^4/ || size eq '20/40'`) is tested
// first, so the 20-ft branch (`size=~/^2/`) only catches a bare 20.
const is40ish = (size: string) => /^4/.test(size) || size === '20/40';
const is20 = (size: string) => /^2/.test(size);

/** lbs overweight limit, or null when the size has no rule. */
export function lbsLimit(size: string, triaxle: boolean, otr: boolean): number | null {
  if (is40ish(size)) return otr ? 45000 : 48500;
  if (is20(size)) {
    if (triaxle) return otr ? 44750 : 47400;
    return otr ? 40000 : 42000;
  }
  return null;
}

/** kgs overweight limit, or null when the size has no rule. */
export function kgsLimit(size: string, triaxle: boolean, otr: boolean): number | null {
  if (is40ish(size)) return otr ? 20412 : 21999.6;
  if (is20(size)) {
    if (triaxle) return otr ? 20298.6 : 21500.64;
    return otr ? 18144 : 19051.2;
  }
  return null;
}

/** Per-field overweight flags. V1 gates the lbs OTR limit on DIRECT + OTR
 *  specifically; the kgs limit keys on the OTR delivery zone alone. */
export function quoteOverweight(
  f: WeightFields,
  quotetype: string,
): { lbsOver: boolean; kgsOver: boolean } {
  const size = (f.size || '').trim();
  const triaxle = (f.triaxle || '').toUpperCase() === 'Y';
  const otrZone = (f.deliveryZone || '').toUpperCase() === 'OTR';
  const otrLbs = (quotetype || '').toUpperCase() === 'DIRECT' && otrZone;
  const lbs = Number(f.lbs) || 0;
  const kgs = Number(f.kgs) || 0;
  const ll = lbsLimit(size, triaxle, otrLbs);
  const kl = kgsLimit(size, triaxle, otrZone);
  return {
    lbsOver: ll != null && lbs > ll,
    kgsOver: kl != null && kgs > kl,
  };
}
